import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getErrorString } from '../../../utils/errorUtils'
import type { UpdateEventTriggerArg } from '../../../modules/EventTriggers'

const splitToNumberArray = (value: string): number[] => (
  value.split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => Number(item))
    .filter((num) => !Number.isNaN(num))
)

const splitToStringArray = (value: string): string[] => (
  value.split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
)

const formatMamoriosForInput = (
  mamorios?: IEventTriggerMamorio[],
  fallbackIds?: (string | number)[],
): string => {
  if (mamorios && mamorios.length > 0) {
    return mamorios
      .map((mamorio) => {
        if (mamorio.major !== null && mamorio.minor !== null) {
          return `${mamorio.major}-${mamorio.minor}`
        }
        if (mamorio.major !== null) {
          return String(mamorio.major)
        }
        if (mamorio.minor !== null) {
          return String(mamorio.minor)
        }
        return String(mamorio.id)
      })
      .join(', ')
  }

  if (fallbackIds && fallbackIds.length > 0) {
    return fallbackIds.map((value) => String(value)).join(', ')
  }

  return ''
}

const parseMamorioIdentifiers = (value: string): (string | number)[] => (
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => {
      if (item.includes('-')) return item
      const numeric = Number(item)
      return Number.isNaN(numeric) ? item : numeric
    })
)

type Props = {
  closeModalFunction: (arg: unknown | void) => void
  modalProps: {
    trigger: IStayNotification
    onSubmit: (payload: UpdateEventTriggerArg) => Promise<void>
    group?: IGroup | null
  }
}

const EditStayNotificationModal = ({ closeModalFunction, modalProps }: Props) => {
  const { trigger, onSubmit, group } = modalProps

  const [name, setName] = useState(trigger.name ?? '')
  const [mailSubject, setMailSubject] = useState(trigger.mail_subject ?? '')
  const [mailBody, setMailBody] = useState(trigger.mail_body ?? '')
  const [cooldown, setCooldown] = useState(
    trigger.cooldown_min !== null && trigger.cooldown_min !== undefined
      ? String(trigger.cooldown_min)
      : '',
  )
  const [thresholdMinutes, setThresholdMinutes] = useState(
    trigger.threshold_minutes !== null && trigger.threshold_minutes !== undefined
      ? String(trigger.threshold_minutes)
      : '',
  )
  const [maxGapSeconds, setMaxGapSeconds] = useState(
    trigger.max_gap_seconds !== null && trigger.max_gap_seconds !== undefined
      ? String(trigger.max_gap_seconds)
      : '',
  )
  const [active, setActive] = useState(trigger.active)
  const [phoneNotification, setPhoneNotification] = useState(trigger.phone_notification)
  const [phoneText, setPhoneText] = useState(trigger.phone_text ?? '')
  const [emails, setEmails] = useState(() => (
    (trigger.emails && trigger.emails.length > 0
      ? trigger.emails
      : trigger.users?.map((user) => user.email) ?? [])
      .join(', ')
  ))
  const [slackWebhook, setSlackWebhook] = useState(trigger.slack_webhook_url ?? '')
  const [mamorioIds, setMamorioIds] = useState(
    formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids),
  )
  const [deviceIds, setDeviceIds] = useState(
    trigger.device_ids?.length ? trigger.device_ids.join(', ') : '',
  )
  const [userIds, setUserIds] = useState(
    trigger.user_ids?.length ? trigger.user_ids.join(', ') : '',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const isMountedRef = useRef(true)
  const groupDisplayName = useMemo(() => (
    group?.name ?? `ID: ${trigger.group_id}`
  ), [group, trigger.group_id])

  useEffect(() => {
    setMamorioIds(formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids))
  }, [trigger.id, trigger.mamorio_ids, trigger.mamorios])

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const titleLabel = useMemo(() => '滞在時間通知', [])

  const buildPayload = useCallback((): UpdateEventTriggerArg => {
    const parsedCooldown = cooldown.trim().length === 0 ? null : Number(cooldown)
    const parsedThreshold = thresholdMinutes.trim().length === 0 ? null : Number(thresholdMinutes)
    const parsedMaxGap = maxGapSeconds.trim().length === 0 ? null : Number(maxGapSeconds)

    return {
      kind: 'stay',
      id: trigger.id,
      params: {
        group_id: trigger.group_id,
        name: name.trim(),
        mail_subject: mailSubject.trim() || null,
        mail_body: mailBody.trim() || null,
        cooldown_min: Number.isNaN(parsedCooldown) ? null : parsedCooldown,
        active,
        phone_notification: phoneNotification,
        phone_text: phoneText.trim() || null,
        emails: splitToStringArray(emails),
        slack_webhook_url: slackWebhook.trim() || null,
        threshold_minutes: Number.isNaN(parsedThreshold) ? null : parsedThreshold,
        max_gap_seconds: Number.isNaN(parsedMaxGap) ? null : parsedMaxGap,
        mamorio_ids: parseMamorioIdentifiers(mamorioIds),
        device_ids: splitToNumberArray(deviceIds),
        user_ids: splitToNumberArray(userIds),
      },
    }
  }, [active, cooldown, deviceIds, emails, mailBody, mailSubject, maxGapSeconds, name, phoneNotification, phoneText, slackWebhook, thresholdMinutes, trigger.group_id, trigger.id, mamorioIds, userIds])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage(null)

    const payload = buildPayload()

    try {
      await onSubmit(payload)
      closeModalFunction(undefined)
    } catch (error: unknown) {
      if (isMountedRef.current) {
        setErrorMessage(getErrorString(error))
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false)
      }
    }
  }

  const isFormLocked = !active

  type FieldLayoutProps = {
    id: string
    label: string
    description?: string
    control: React.ReactNode
    helper?: React.ReactNode
    isCheckbox?: boolean
  }

  const renderField = ({
    id,
    label,
    description,
    control,
    helper,
    isCheckbox = false,
  }: FieldLayoutProps) => (
    <div className={`event-trigger-modal__field${isCheckbox ? ' event-trigger-modal__field--checkbox' : ''}`} key={id}>
      <div className="event-trigger-modal__field-label">
        <label htmlFor={id}>{label}</label>
        {description && (
          <p className="event-trigger-modal__field-description">{description}</p>
        )}
      </div>
      <div className="event-trigger-modal__field-control">
        {control}
        {helper}
      </div>
    </div>
  )

  const helperList = (
    <div className="event-trigger-modal__helper">
      <div className="event-trigger-modal__helper-title">
        挿入可能な情報
      </div>
      <ul>
        <li>
          <code>{'{EVENT_NAME}'}</code>
          <span>通知名</span>
        </li>
        <li>
          <code>{'{MAMORIO_NAME}'}</code>
          <span>対象のMAMORIO名</span>
        </li>
        <li>
          <code>{'{DEVICE_NAME}'}</code>
          <span>検知したデバイス名</span>
        </li>
        <li>
          <code>{'{EVENT_TIME}'}</code>
          <span>検知日時（ISO形式）</span>
        </li>
        <li>
          <code>{'{EVENT_TIME_DATE}'}</code>
          <span>検知日（YYYY-MM-DD）</span>
        </li>
        <li>
          <code>{'{EVENT_TIME_TIME}'}</code>
          <span>検知時刻（HH:MM:SS）</span>
        </li>
      </ul>
    </div>
  )

  const importantFields: FieldLayoutProps[] = [
    {
      id: 'stay-notification-group-name',
      label: '対象グループ',
      control: (
        <span id="stay-notification-group-name" className="event-trigger-modal__group-chip">
          {groupDisplayName}
        </span>
      ),
    },
    {
      id: 'stay-notification-name',
      label: 'トリガー名称',
      description: '一覧に表示される管理用の名前です。運用メンバーが識別しやすい名称を設定してください。',
      control: (
        <input
          id="stay-notification-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="名称"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-active',
      label: '有効にする',
      description: '通知の配信を開始します。チェックを外すと一時停止し、設定の編集ができなくなります。',
      control: (
        <input
          id="stay-notification-active"
          type="checkbox"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />
      ),
      isCheckbox: true,
    },
    {
      id: 'stay-notification-threshold',
      label: '滞在閾値 (分)',
      description: 'MAMORIOの滞在を通知するまでに必要な滞在時間です。設定分数を超えると通知します。',
      control: (
        <input
          id="stay-notification-threshold"
          type="number"
          min={0}
          value={thresholdMinutes}
          onChange={(event) => setThresholdMinutes(event.target.value)}
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-max-gap',
      label: '許容検知間隔 (秒)',
      description: '滞在とみなすために連続検知の間隔として許容する秒数です。',
      control: (
        <input
          id="stay-notification-max-gap"
          type="number"
          min={0}
          value={maxGapSeconds}
          onChange={(event) => setMaxGapSeconds(event.target.value)}
          disabled={isFormLocked}
        />
      ),
    },
  ]

  const messagingFields: FieldLayoutProps[] = [
    {
      id: 'stay-notification-mailsubject',
      label: 'メール件名',
      description: 'メール通知の件名です。',
      control: (
        <input
          id="stay-notification-mailsubject"
          type="text"
          value={mailSubject}
          onChange={(event) => setMailSubject(event.target.value)}
          placeholder="メール件名"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-mailbody',
      label: 'メール本文',
      description: '通知メールの本文です。テンプレート変数を使って滞在情報を差し込めます。',
      control: (
        <textarea
          id="stay-notification-mailbody"
          rows={3}
          value={mailBody}
          onChange={(event) => setMailBody(event.target.value)}
          placeholder="メール本文"
          disabled={isFormLocked}
        />
      ),
      helper: helperList,
    },
    {
      id: 'stay-notification-emails',
      label: '通知先メールアドレス',
      description: '通知を送信するメールアドレスをカンマ区切りで指定します。',
      control: (
        <input
          id="stay-notification-emails"
          type="text"
          value={emails}
          onChange={(event) => setEmails(event.target.value)}
          placeholder="example@mamorio.jp, user@example.com"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-slack',
      label: 'Slack Webhook URL',
      description: 'Slack通知を利用する場合のIncoming Webhook URLです。',
      control: (
        <input
          id="stay-notification-slack"
          type="url"
          value={slackWebhook}
          onChange={(event) => setSlackWebhook(event.target.value)}
          placeholder="https://hooks.slack.com/services/..."
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-phone-notification',
      label: '電話通知を送る',
      description: '音声通話で滞在通知を行います。読み上げるメッセージは下で設定します。',
      control: (
        <input
          id="stay-notification-phone-notification"
          type="checkbox"
          checked={phoneNotification}
          onChange={(event) => setPhoneNotification(event.target.checked)}
          disabled={isFormLocked}
        />
      ),
      isCheckbox: true,
    },
    {
      id: 'stay-notification-phone-text',
      label: '電話通知メッセージ',
      description: '電話発報時に読み上げる文言です。',
      control: (
        <input
          id="stay-notification-phone-text"
          type="text"
          value={phoneText}
          onChange={(event) => setPhoneText(event.target.value)}
          placeholder="電話通知の文面"
          disabled={isFormLocked || !phoneNotification}
        />
      ),
    },
    {
      id: 'stay-notification-user-ids',
      label: '通知先ユーザーID (カンマ区切り)',
      description: '特定のユーザーIDに限定して通知したい場合に設定します。',
      control: (
        <input
          id="stay-notification-user-ids"
          type="text"
          value={userIds}
          onChange={(event) => setUserIds(event.target.value)}
          placeholder="201, 202"
          disabled={isFormLocked}
        />
      ),
    },
  ]

  const advancedFields: FieldLayoutProps[] = [
    {
      id: 'stay-notification-cooldown',
      label: 'クールダウン (分)',
      description: '通知後に再通知を抑止する分数です。短時間での通知連発を避けたいときに設定します。',
      control: (
        <input
          id="stay-notification-cooldown"
          type="number"
          min={0}
          value={cooldown}
          onChange={(event) => setCooldown(event.target.value)}
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-mamorio-ids',
      label: '対象MAMORIO ID (カンマ区切り)',
      description: '滞在を監視するMAMORIOを限定したい場合にIDまたはmajor-minor形式で入力します。',
      control: (
        <input
          id="stay-notification-mamorio-ids"
          type="text"
          value={mamorioIds}
          onChange={(event) => setMamorioIds(event.target.value)}
          placeholder="111-222, 333-444"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'stay-notification-device-ids',
      label: '対象デバイスID (カンマ区切り)',
      description: '滞在計測の対象となるSpotデバイスを指定します。',
      control: (
        <input
          id="stay-notification-device-ids"
          type="text"
          value={deviceIds}
          onChange={(event) => setDeviceIds(event.target.value)}
          placeholder="42, 43"
          disabled={isFormLocked}
        />
      ),
    },
  ]

  return (
    <form className="event-trigger-modal modal-form" onSubmit={handleSubmit}>
      <div className="event-trigger-modal__header">
        <div className="event-trigger-modal__legend">
          {titleLabel}
        </div>
        <p className="event-trigger-modal__description">
          MAMORIOがデバイス付近に一定時間滞留した際にメール・電話・Slackで通知します。閾値や許容検知間隔を調整して運用に合わせた警告を設定できます。
        </p>
      </div>
      {isFormLocked && (
        <div className="event-trigger-modal__lock-banner">
          現在この通知は無効です。「有効にする」にチェックを入れると設定項目が編集できるようになります。
        </div>
      )}
      <section className="event-trigger-modal__section">
        <div className="event-trigger-modal__section-body">
          {importantFields.map((field) => renderField(field))}
        </div>
      </section>
      <section className="event-trigger-modal__section">
        <h3 className="event-trigger-modal__section-title">メッセージ送信先と内容</h3>
        <div className="event-trigger-modal__section-body">
          {messagingFields.map((field) => renderField(field))}
        </div>
      </section>
      <section className="event-trigger-modal__section">
        <h3 className="event-trigger-modal__section-title">詳細設定</h3>
        <div className="event-trigger-modal__section-body">
          {advancedFields.map((field) => renderField(field))}
        </div>
      </section>
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        className="btn btn-primary"
        disabled={isSubmitting}
      >
        保存する
      </button>
      <button
        type="button"
        className="btn btn-outline-primary cancel-button"
        onClick={() => closeModalFunction(undefined)}
      >
        キャンセル
      </button>
    </form>
  )
}

export default EditStayNotificationModal
